import { makeStyles } from '@material-ui/core'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import Dialog from '@material-ui/core/Dialog'
import DialogContent from '@material-ui/core/DialogContent'
import DialogContentText from '@material-ui/core/DialogContentText'
import DialogTitle from '@material-ui/core/DialogTitle'
import { get } from 'lodash'
import { useState } from 'react'
import {
  Button as RaButton,
  FileField,
  FileInput,
  Labeled,
  SelectInput,
  useNotify,
  useRefresh,
} from 'react-admin'
import { Form } from 'react-final-form'

import { TEAM } from 'helpers/documents'
import { captureExceptionIfProduction } from 'helpers/utils'
import { uploadTeamDocumentation } from '../../api/core'
import { withDisableBackdropClick } from '../../helpers/withDisableBackdropClick'
import { ProductSubscription } from '../../models/subscription'
import { hasPermission, Permissions } from '../../Permissions'
import DocumentList from '../Documents/DocumentList'
import TeamTaxForm from './TeamTaxForm'

const TeamDocumentType = {
  BusinessDocumentation: 'business_documentation',
  CertificateOfInsurance: 'certificate_of_insurance',
  OperatingAuthorityLetter: 'operating_authority_letter',
  LetterOfRelease: 'letter_of_release',
  NoticeOfAssignment: 'notice_of_assignment',
}

const BUSINESS_TYPE: string = 'business_type'

const TeamDocumentTypeLabel = {
  [TeamDocumentType.BusinessDocumentation]: 'Business Documentation',
  [TeamDocumentType.CertificateOfInsurance]: 'Certificate of Insurance',
  [TeamDocumentType.OperatingAuthorityLetter]: 'Operating Authority Letter',
  [TeamDocumentType.LetterOfRelease]: 'Letter of Release',
  [TeamDocumentType.NoticeOfAssignment]: 'Notice of Assignment',
}

const BusinessTypeChoices = [
  { id: 'CORP', name: 'Corporation' },
  { id: 'LLC', name: 'LLC' },
  { id: 'SP', name: 'Sole Proprietorship' },
]

const EditTeamDocumentButton = ({ record, type }: any) => {
  const [showDialog, setShowDialog] = useState(false)
  const [loading, setLoading] = useState(false)
  const notify = useNotify()
  const refresh = useRefresh()
  const handleClick = () => setShowDialog(true)
  const handleClose = () => setShowDialog(false)

  const useStyles = makeStyles({
    saveButton: {
      backgroundColor: '#002CB1',
      color: 'white',
      borderRadius: '570px',
      width: '40%',
    },
  })

  const classes = useStyles()

  const initialValues: any = {
    [type]: get(record, type),
  }

  const isBusinessDocumentation = type === TeamDocumentType.BusinessDocumentation

  if (isBusinessDocumentation) {
    initialValues[BUSINESS_TYPE] = get(record, BUSINESS_TYPE)
  }

  const dialogTitle = `Edit ${TeamDocumentTypeLabel[type]}`

  const handleSubmit = async (values: any) => {
    setLoading(true)

    const formData = new FormData()
    let fileValue = get(values, type)
    if (fileValue !== null) {
      fileValue = fileValue.rawFile
    }
    formData.append('documentation_file', fileValue)
    formData.append('documentation_field', type)

    if (isBusinessDocumentation) {
      formData.append(BUSINESS_TYPE, get(values, BUSINESS_TYPE))
    }

    uploadTeamDocumentation(record.id, formData)
      .then(() => {
        setShowDialog(false)
        setLoading(false)
        notify('Document updated!', { type: 'success' })
        refresh()
      })
      .catch((error) => {
        notify(`Oops! Something went wrong: ${JSON.stringify(error.response.data)}`, { type: 'error' })
        captureExceptionIfProduction(error)
        setLoading(false)
      })
  }

  return (
    <>
      <RaButton onClick={handleClick} label="Edit" />
      <Dialog open={showDialog} onClose={withDisableBackdropClick(handleClose)} aria-label={dialogTitle}>
        <DialogTitle>{dialogTitle}</DialogTitle>
        <Form
          initialValues={initialValues}
          onSubmit={handleSubmit}
          render={(formProps) => {
            const formState = formProps.form.getState()
            const disableSave = loading || get(formState.values, type) === get(initialValues, type)
            return (
              <>
                <DialogContent>
                  <DialogContentText>Remove the current document or upload a new one</DialogContentText>
                  <Box display="flex" flexDirection="column" alignItems="center">
                    <FileInput source={type}>
                      <FileField title={type} />
                    </FileInput>
                    {isBusinessDocumentation && (
                      <SelectInput source="business_type" choices={BusinessTypeChoices} fullWidth={true} />
                    )}
                    <Button
                      type="submit"
                      onClick={formProps.handleSubmit}
                      disabled={disableSave}
                      variant="contained"
                      className={classes.saveButton}
                    >
                      Save
                    </Button>
                    <Button onClick={handleClose} disabled={loading}>
                      Cancel
                    </Button>
                  </Box>
                </DialogContent>
              </>
            )
          }}
        />
      </Dialog>
    </>
  )
}

function getBusinessTypeString(type: any) {
  const businessType = BusinessTypeChoices.find((value) => value.id === type)
  return businessType ? businessType.name : 'N/A'
}

const TeamDocumentField = ({ record, type, permissions }: any) => {
  const canChangeTeam = hasPermission(permissions, Permissions.ChangeTeam)

  const title =
    type === TeamDocumentType.BusinessDocumentation
      ? getBusinessTypeString(get(record, 'business_type'))
      : 'View file'

  return (
    <Box display="flex" flexDirection="row" alignItems="center">
      <Box flex={1}>
        <Labeled label={TeamDocumentTypeLabel[type]}>
          <FileField record={record} source={type} title={title} target="_blank" />
        </Labeled>
      </Box>
      {canChangeTeam && (
        <Box flex={3}>
          <EditTeamDocumentButton record={record} type={type} />
        </Box>
      )}
    </Box>
  )
}

const TeamDocuments = ({ record, permissions }: any) => {
  if (!record || !permissions) {
    return null
  }
  if (record.is_premium) {
    return (
      <>
        <TeamDocumentField
          record={record}
          type={TeamDocumentType.BusinessDocumentation}
          permissions={permissions}
        />
        <TeamTaxForm record={record} permissions={permissions} />
        <DocumentList
          record={record}
          folderName={TEAM}
          permissions={permissions}
          multipleAttachments={false}
          isTeamPage={true}
        />
      </>
    )
  }

  const isCash = record.product_subscription.product_name === ProductSubscription.CASH
  const isFlex = record.product_subscription.product_name === ProductSubscription.FLEX

  return (
    <>
      <TeamDocumentField
        record={record}
        type={TeamDocumentType.BusinessDocumentation}
        permissions={permissions}
      />
      {!isCash && (
        <TeamDocumentField
          record={record}
          type={TeamDocumentType.CertificateOfInsurance}
          permissions={permissions}
        />
      )}
      <TeamDocumentField
        record={record}
        type={TeamDocumentType.OperatingAuthorityLetter}
        permissions={permissions}
      />
      <TeamTaxForm record={record} permissions={permissions} />
      {(isCash || isFlex) && (
        <>
          <TeamDocumentField
            record={record}
            type={TeamDocumentType.LetterOfRelease}
            permissions={permissions}
          />
          <TeamDocumentField
            record={record}
            type={TeamDocumentType.NoticeOfAssignment}
            permissions={permissions}
          />
        </>
      )}
    </>
  )
}

export default TeamDocuments
